import {
  ECOLABEL_KEYS,
  type MandateQualities,
  type PeriodVolume,
} from "@/forwards2/forwardsTypes";
import {
  monthsOfWidth,
  parsePeriodLabel,
  periodEndDate,
} from "@/forwards2/periodUtils";
import { splitTakeOverPeriods } from "@/forwards2/picker/distributeTakes";
import { technologyName } from "@/types/technology";
import { sumBy } from "lodash-es";

/**
 * One mandate's take beside its capacity per period of its cover of the range — the slices the take
 * will be split into, at the mandate's own width (`2027-Q1`, `2027-H1` or `2027`).
 */
export interface CoveredTake {
  take: number;
  capacity_by_period: Readonly<Record<string, number>>;
}

export interface BreakdownEntry {
  label: string;
  mwh: number;
}

/**
 * One mandate's volume in what is being broken down, with the qualities it is grouped by.
 *
 * @property priceLabel - The mandate's price condition; the price group is built only when every
 *   share names one
 */
export interface BreakdownShare {
  mwh: number;
  qualities: MandateQualities;
  priceLabel?: string;
}

/**
 * @property judgedAtQualificationDate - The group's values depend on the day they are judged at
 *   (COD age, RE100, OK-power "new"), which the header names
 */
export interface BreakdownGroup {
  key: string;
  title: string;
  judgedAtQualificationDate: boolean;
  entries: BreakdownEntry[];
}

type Translate = (key: string) => string;

/**
 * The takes' estimated MWh per vintage: each take split over its own mandate's cover by
 * {@link splitTakeOverPeriods}, summed per period by {@link volumeByPeriod}.
 */
export function volumeByVintage(
  takes: readonly CoveredTake[]
): BreakdownEntry[] {
  return volumeByPeriod(
    takes.flatMap(({ take, capacity_by_period }) =>
      Object.entries(splitTakeOverPeriods(take, capacity_by_period)).map(
        ([period_label, mwh]) => ({ period_label, mwh })
      )
    )
  );
}

/**
 * Sums the volume of each period, ordered by the delivery the period lands in, narrower periods
 * first, so `2027-Q2` and `2027-H1` sit together before `2027-Q3`.
 */
export function volumeByPeriod(
  volumes: readonly PeriodVolume[]
): BreakdownEntry[] {
  const totals = new Map<string, number>();
  for (const { period_label, mwh } of volumes) {
    totals.set(period_label, (totals.get(period_label) ?? 0) + mwh);
  }
  return [...totals.entries()]
    .sort(([left], [right]) => compareByDelivery(left, right))
    .map(([label, mwh]) => ({ label, mwh }));
}

function compareByDelivery(left: string, right: string): number {
  const byEnd = (periodEndDate(left) ?? left).localeCompare(
    periodEndDate(right) ?? right
  );
  return byEnd !== 0 ? byEnd : widthMonths(left) - widthMonths(right);
}

function widthMonths(label: string): number {
  const parsed = parsePeriodLabel(label);
  return parsed ? monthsOfWidth(parsed.width) : 0;
}

/**
 * Volume grouped by the qualities of the mandates it comes from — the volume picker's summary of a
 * selection, and a planned delivery's contents.
 *
 * A mandate's qualities hold all-or-nothing over its devices, so its whole volume lands on one entry
 * of each group: a mandate spanning two countries is reported as that pair rather than split.
 * Ecolabel entries overlap (a mandate can hold several), so that group does not add up; the others
 * do, with `unbackedMwh` as an entry of its own when there is any.
 */
export function breakdownGroups(
  shares: readonly BreakdownShare[],
  unbackedMwh: number,
  t: Translate
): BreakdownGroup[] {
  const groupBy = (labelOf: (qualities: MandateQualities) => string) => {
    const entries = groupShares(shares, (share) => labelOf(share.qualities));
    return unbackedMwh > 0
      ? [
          ...entries,
          { label: t("forwards.common.not_backed"), mwh: unbackedMwh },
        ]
      : entries;
  };
  const priced =
    shares.length > 0 &&
    shares.every((share) => share.priceLabel !== undefined);

  return [
    {
      key: "country",
      title: t("forwards.picker.summary.by_country"),
      judgedAtQualificationDate: false,
      entries: groupBy(({ countries }) =>
        countries.length > 0
          ? countries.join("/")
          : t("forwards.common.unknown")
      ),
    },
    ...(priced
      ? [
          {
            key: "price",
            title: t("forwards.picker.summary.by_price"),
            judgedAtQualificationDate: false,
            entries: groupShares(shares, (share) => share.priceLabel!),
          },
        ]
      : []),
    {
      key: "technology",
      title: t("forwards.picker.summary.by_technology"),
      judgedAtQualificationDate: false,
      entries: groupBy(({ technologies }) =>
        technologies.length > 0
          ? technologies.map(technologyName).join(", ")
          : t("forwards.common.unknown")
      ),
    },
    {
      key: "cod",
      title: t("forwards.picker.summary.by_cod"),
      judgedAtQualificationDate: true,
      entries: groupBy(({ oldest_cod_age: age }) =>
        age == null
          ? t("forwards.common.unknown")
          : t(
              `forwards.picker.summary.${age <= 5 ? "cod_young" : age <= 15 ? "cod_middle" : "cod_old"}`
            )
      ),
    },
    {
      key: "re100",
      title: t("forwards.picker.summary.re100"),
      judgedAtQualificationDate: true,
      entries: groupBy(({ re100_applicable: applicable }) =>
        t(
          applicable == null
            ? "common.unknown"
            : applicable
              ? "common.yes"
              : "common.no"
        )
      ),
    },
    {
      key: "ecolabels",
      title: t("forwards.picker.summary.by_ecolabel"),
      judgedAtQualificationDate: true,
      entries: ECOLABEL_KEYS.map((key) => ({
        label: t(`forwards.picker.ecolabel.${key}`),
        mwh: sumBy(
          shares.filter((share) => share.qualities.ecolabels.includes(key)),
          "mwh"
        ),
      })),
    },
    {
      key: "support",
      title: t("forwards.picker.summary.by_support"),
      judgedAtQualificationDate: false,
      entries: groupBy(({ support }) =>
        t(`forwards.common.support.${support}`)
      ),
    },
  ];
}

/** Sums the volume of every share with one label, biggest first. */
function groupShares(
  shares: readonly BreakdownShare[],
  labelOf: (share: BreakdownShare) => string
): BreakdownEntry[] {
  const totals = new Map<string, number>();
  for (const share of shares) {
    const label = labelOf(share);
    totals.set(label, (totals.get(label) ?? 0) + share.mwh);
  }
  return [...totals.entries()]
    .map(([label, mwh]) => ({ label, mwh }))
    .sort((left, right) => right.mwh - left.mwh);
}
